import { useEffect, useState, useCallback } from "react";
import { globalEventBus } from "@/lib/realtime/EventBus.js";
import { useRealtime } from "@/hooks/useRealtime.js";
import { useColyseus } from "@/hooks/useColyseus.js";

/**
 * useRealtimeTables
 * - Subscribes to table broadcasts from the realtime adapter
 * - Handles: tables, tableCreated, tableUpdated, tableDeleted events
 * - Exposes normalized tables array and methods to emit table actions
 * - Uses Colyseus as primary transport when available
 */
export function useRealtimeTables() {
  const [tables, setTables] = useState([]);
  const realtime = useRealtime();
  const colyseus = useColyseus();

  // Sync directly with Colyseus lobbyState when available
  useEffect(() => {
    if (colyseus?.connected && Array.isArray(colyseus?.lobbyState?.tables)) {
      const tablesList = colyseus.lobbyState.tables;
      setTables([...tablesList]); // Create new array to trigger updates
    }
  }, [colyseus?.connected, colyseus?.lobbyState]);

  useEffect(() => {
    // Handler for full table list (tables snapshot)
    const handleTables = (tablesList) => {
      if (Array.isArray(tablesList)) {
        setTables(tablesList);
      }
    };

    // Handler for single table created
    const handleTableCreated = ({ table }) => {
      if (table) {
        setTables((prev) => {
          // Check if already exists
          const exists = prev.some((t) => t.id === table.id);
          if (exists) {
            return prev;
          }
          return [...prev, table];
        });
      }
    };

    // Handler for single table updated
    const handleTableUpdated = ({ table }) => {
      if (table) {
        setTables((prev) =>
          prev.map((t) => (t.id === table.id ? { ...t, ...table } : t))
        );
      }
    };

    // Handler for table deleted
    const handleTableDeleted = ({ tableId }) => {
      if (tableId) {
        setTables((prev) => prev.filter((t) => t.id !== tableId));
      }
    };

    // Subscribe to all table events
    const unsubTables = globalEventBus.on("tables", handleTables);
    const unsubCreated = globalEventBus.on("tableCreated", handleTableCreated);
    const unsubUpdated = globalEventBus.on("tableUpdated", handleTableUpdated);
    const unsubDeleted = globalEventBus.on("tableDeleted", handleTableDeleted);

    // Request initial tables on mount
    if (realtime?.emit) {
      realtime.emit("getTables");
    }

    return () => {
      unsubTables();
      unsubCreated();
      unsubUpdated();
      unsubDeleted();
    };
  }, [realtime]);

  const emitSafe = useCallback(
    (event, payload) => {
      try {
        if (!realtime?.emit) return false;
        return realtime.emit(event, payload);
      } catch {
        return false;
      }
    },
    [realtime]
  );

  const createTable = useCallback(
    (tableData) => {
      if (import.meta?.env?.DEV) {
        console.warn("🎲 [useRealtimeTables] createTable called:", tableData);
      }

      // Try Colyseus first (preferred)
      if (colyseus?.connected && colyseus?.createTable) {
        if (import.meta?.env?.DEV) {
          console.warn("🎲 [useRealtimeTables] Using Colyseus to create table");
        }
        return colyseus.createTable(tableData);
      }

      // Fallback to legacy WebSocket
      if (import.meta?.env?.DEV) {
        console.warn("🎲 [useRealtimeTables] Falling back to legacy WebSocket");
      }
      const result = emitSafe("createTable", { tableData });
      if (import.meta?.env?.DEV) {
        console.warn("🎲 [useRealtimeTables] emitSafe result:", result);
      }
      return result;
    },
    [colyseus, emitSafe]
  );

  const joinGameTable = useCallback(
    (tableId, password = null) => {
      // Try Colyseus first (preferred)
      if (colyseus?.connected && colyseus?.joinTable) {
        if (import.meta?.env?.DEV) {
          console.log(
            "[useRealtimeTables] Using Colyseus to join table:",
            tableId
          );
        }
        return colyseus.joinTable(tableId);
      }

      // Fallback to legacy WebSocket
      if (import.meta?.env?.DEV) {
        console.log(
          "[useRealtimeTables] Falling back to legacy WebSocket for join"
        );
      }
      return emitSafe("joinGameTable", { tableId, password });
    },
    [colyseus, emitSafe]
  );

  const leaveGameTable = useCallback(
    (tableId) => {
      return emitSafe("leaveGameTable", { tableId });
    },
    [emitSafe]
  );

  const startGame = useCallback(
    (tableId) => {
      return emitSafe("startGame", { tableId });
    },
    [emitSafe]
  );

  const byId = useCallback(
    (id) => tables.find((t) => t.id === id) || null,
    [tables]
  );

  const byStatus = useCallback(
    (status) => tables.filter((t) => t.status === status),
    [tables]
  );

  return {
    isAvailable: !!colyseus?.connected || !!realtime?.emit,
    tables,
    count: tables.length,
    byId,
    byStatus,
    createTable,
    joinGameTable,
    leaveGameTable,
    startGame,
  };
}

export default useRealtimeTables;
